'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import { TierBenefit } from '@flipster/apis/v1/vip/types';
import { T } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import ArrowTrendingDownIcon from '@flipster/react/icon/generated/ArrowTrendingDownIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { flex, grid, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { GlassCard } from '../../shared/glass-card';
import { SimpleCard } from '../../shared/simple-card';
import { vipProgramPageBunja } from '../../state';
import { VolumeSlider } from './volume-slider';

interface Props {
  css?: Styles;
}

export const FeeCalculatorSection = ({ css: cssProps }: Props) => {
  const { vipTierBenefitsAtom } = useBunja(vipProgramPageBunja);
  const vipTierBenefits = useAtomValue(vipTierBenefitsAtom);

  if (!vipTierBenefits) return null;

  return <FeeCalculatorContent css={cssProps} vipTiers={vipTierBenefits.vipTiers} />;
};

interface ContentProps {
  css?: Styles;
  vipTiers: TierBenefit[];
}

const FeeCalculatorContent = ({ css: cssProps, vipTiers }: ContentProps) => {
  const volumeMarks = useMemo(() => {
    return vipTiers
      .map((tier) => (tier.requiredTradingVolume ? parseFloat(tier.requiredTradingVolume) : 0))
      .sort((a, b) => a - b);
  }, [vipTiers]);

  const [volume, setVolume] = useState(volumeMarks[Math.floor(volumeMarks.length / 2)] ?? 0);

  const currentTier = useMemo(() => {
    let matchedTier = vipTiers[0];
    for (const tier of vipTiers) {
      const threshold = tier.requiredTradingVolume ? parseFloat(tier.requiredTradingVolume) : 0;
      if (volume >= threshold) {
        matchedTier = tier;
      }
    }
    return matchedTier;
  }, [volume, vipTiers]);

  const baseTier = vipTiers.find((tier) => tier.vipTier === 0);
  const baseMakerFee = baseTier ? Num(baseTier.pswapMakerFee) : Num(0);
  const baseTakerFee = baseTier ? Num(baseTier.pswapTakerFee) : Num(0);
  const currentMakerFee = Num(currentTier.pswapMakerFee);
  const currentTakerFee = Num(currentTier.pswapTakerFee);

  const makerDiscount = baseMakerFee.gt(0)
    ? Math.round(Num(1).minus(currentMakerFee.div(baseMakerFee)).times(100).toNumber())
    : 0;
  const takerDiscount = baseTakerFee.gt(0)
    ? Math.round(Num(1).minus(currentTakerFee.div(baseTakerFee)).times(100).toNumber())
    : 0;

  const formattedVolume = volume.toLocaleString('en-US');
  const feePrecision = 3;

  return (
    <section className={css(vstack.raw({ py: { base: 40, tablet: 64 }, px: 16 }), cssProps)}>
      <div
        className={vstack({
          gap: 16,
          pb: { base: 32, tablet: 40 },
          color: 'content.default.level-5',
          textAlign: 'center',
        })}
      >
        <p className={css({ textStyle: 'Title/S', color: 'rgba(255, 208, 132)' })}>
          <T id='VIP.r260303.Fee_calculator' />
        </p>
        <h2 className={css({ textStyle: 'Display/XL' })}>
          <T
            id='VIP.r260303.Unlock_lower_trading_fees'
            values={{
              vip: (chunks: React.ReactNode) => <span className={css({ color: 'rgba(255, 208, 132)' })}>{chunks}</span>,
            }}
          />
        </h2>
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
          <T id='VIP.r260303.Slide_to_see_how_much_you_can_save_based_on_your_30_day_trading_volume' />
        </p>
      </div>

      <GlassCard css={{ p: { base: 24, tablet: 48 }, gap: 48, w: '100%' }}>
        <div className={stack({ gap: 8 })}>
          <div
            className={flex({
              tabletDown: { flexDir: 'column', gap: 8 },
              tablet: { flexDir: 'row', justifyContent: 'space-between', alignItems: 'center' },
            })}
          >
            <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
              <T id='VIP.r260303.Thirty_day_trading_volume' />
            </p>
            <Formatted.Number
              value={formattedVolume}
              suffix={UsdtCurrency}
              css={{ textStyle: 'Title/M/Monospace', color: 'rgba(255, 208, 132)' }}
            />
          </div>
          <VolumeSlider marks={volumeMarks} value={volume} onChange={setVolume} />
        </div>

        <div className={stack({ gap: 24 })}>
          <p className={css({ textStyle: 'Title/M' })}>
            <T
              id='VIP.r260303.Your_tier_vip_tier'
              values={{
                tier: currentTier.vipTier,
                vip: (chunks: React.ReactNode) => <span className={css({ color: '#FFD084' })}>{chunks}</span>,
              }}
            />
          </p>

          <div
            className={grid({
              gridTemplateColumns: { base: '1fr', tablet: 'repeat(2, 1fr)' },
              gap: { base: 12, tablet: 16 },
            })}
          >
            <FeeCard
              label={
                <>
                  <T id='Vip.r260121.Maker_fee' /> <T id='Vip.r260121.Futures_parentheses' />
                </>
              }
              baseFee={`${baseMakerFee.times(100).toFixed(feePrecision)}%`}
              currentFee={`${currentMakerFee.times(100).toFixed(feePrecision)}%`}
              discount={makerDiscount}
            />
            <FeeCard
              label={
                <>
                  <T id='Vip.r260121.Taker_fee' /> <T id='Vip.r260121.Spot_futures' />
                </>
              }
              baseFee={`${baseTakerFee.times(100).toFixed(feePrecision)}%`}
              currentFee={`${currentTakerFee.times(100).toFixed(feePrecision)}%`}
              discount={takerDiscount}
            />
          </div>
        </div>
      </GlassCard>
    </section>
  );
};

interface FeeCardProps {
  label: React.ReactNode;
  baseFee: string;
  currentFee: string;
  discount: number;
}

const FeeCard = ({ label, baseFee, currentFee, discount }: FeeCardProps) => (
  <SimpleCard css={{ p: { base: 16, tablet: 20 }, gap: 12 }}>
    <div className={hstack({ gap: 8 })}>
      <ArrowTrendingDownIcon size={20} color='rgba(255, 208, 132)' />
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>{label}</p>
    </div>
    <div className={stack({ gap: 12 })}>
      <div className={hstack({ gap: 8, alignItems: 'center' })}>
        {discount > 0 && (
          <>
            {' '}
            <span
              className={css({
                textStyle: 'Text/L/Monospace/Regular',
                color: 'content.default.level-1',
                textDecoration: 'line-through',
              })}
            >
              {baseFee}
            </span>
            <ArrowRightIcon className={css({ w: 14, h: 14 })} color={token('colors.content.default.level-2')} />
          </>
        )}

        <span className={css({ textStyle: 'Title/M', color: 'content.default.level-5' })}>{currentFee}</span>
      </div>
      {discount > 0 && (
        <p className={css({ textStyle: 'Text/M/Strong', color: 'rgba(255, 208, 132)' })}>
          <T id='VIP.r260304.Save_percent' values={{ percent: discount }} />
        </p>
      )}
    </div>
  </SimpleCard>
);
